import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import DashShell from '../dash/DashShell';

interface VPSInstance {
  id: string;
  name: string;
  container_name: string;
  status: 'creating' | 'running' | 'stopped' | 'suspended' | 'terminated' | 'failed';
  ipv4_address: string | null;
  ipv6_address: string | null;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  plan_name: string;
  monthly_price: number;
  server_name: string;
  server_location: string;
  os_template: string;
  created_at: string;
  uptime?: number;
  cpu_usage?: number;
  memory_usage?: number;
  disk_usage?: number;
  network_in?: number;
  network_out?: number;
  root_password?: string;
}

interface VPSMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_in: number;
  network_out: number;
  uptime: number;
  load_average: number[];
}

const AdvancedVPSDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'overview' | 'instances' | 'networking' | 'backups' | 'monitoring'>('instances');
  const [vpsInstances, setVpsInstances] = useState<VPSInstance[]>([]);
  const [selectedVPS, setSelectedVPS] = useState<VPSInstance | null>(null);
  const [vpsMetrics, setVpsMetrics] = useState<{[key: string]: VPSMetrics}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    fetchVPSData();
    const interval = setInterval(fetchVPSData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchVPSData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch VPS instances
      const response = await fetch(getApiUrl('/vps-subscriptions/my-subscriptions'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const instances = data.data?.map((sub: any) => ({
          id: sub.vps_id,
          name: sub.vps_name,
          status: sub.vps_status,
          plan_name: sub.plan_name,
          cpu_cores: sub.cpu_cores,
          memory_mb: sub.memory_mb,
          disk_gb: sub.disk_gb,
          ipv4_address: sub.ipv4_address,
          hostname: sub.hostname,
          os_template: sub.os_template,
          container_name: sub.container_name,
          created_at: sub.created_at,
          monthly_price: parseFloat(sub.amount || '0'),
          server_name: 'VPS-Genesis',
          server_location: 'New York, USA'
        })) || [];
        
        setVpsInstances(instances);
        
        // Fetch metrics for each VPS
        instances.forEach((vps: VPSInstance) => {
          fetchVPSMetrics(vps.id);
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching VPS data:', error);
      setError('Failed to load VPS data');
      setLoading(false);
    }
  };

  const fetchVPSMetrics = async (vpsId: string) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(getApiUrl(`/vps/${vpsId}/live`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return; // leave the previous reading; don't blank out the UI
      const json = await r.json();
      const d = json?.data || {};
      const real: VPSMetrics = {
        cpu_usage: Number(d.cpu_usage || 0),
        memory_usage: Number(d.memory_usage || 0),
        disk_usage: Number(d.disk_usage || 0),
        network_in: Number(d.network_in_bytes || 0),
        network_out: Number(d.network_out_bytes || 0),
        uptime: Number(d.uptime || 0),
        // Proxmox status/current does not expose load average; leave empty
        // until we wire qemu-guest-agent or node-level metrics for it.
        load_average: [],
      };
      setVpsMetrics(prev => ({ ...prev, [vpsId]: real }));
    } catch (err) {
      // Silent on transient errors — next poll will retry.
    }
  };

  const handleVPSControl = async (instanceId: string, action: 'start' | 'stop' | 'restart' | 'destroy') => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = getApiUrl(`/vps/${instanceId}/${action}`);
      
      if (action === 'destroy') {
        if (!window.confirm('Are you sure you want to destroy this VPS? This action cannot be undone!')) {
          return;
        }
        endpoint = getApiUrl(`/vps/${instanceId}`);
      }

      const response = await fetch(endpoint, {
        method: action === 'destroy' ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(`VPS ${action} initiated successfully`);
        fetchVPSData();
      } else {
        setError(result.error || `Failed to ${action} VPS`);
      }
    } catch (error) {
      console.error(`Error ${action} VPS:`, error);
      setError(`Failed to ${action} VPS`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'stopped':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'creating':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'terminated':
      case 'failed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <DashShell title="VPS Management" subtitle="Loading your virtual private servers…">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 rounded-2xl bg-white/60 backdrop-blur" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-white/60 backdrop-blur" />
            ))}
          </div>
        </div>
      </DashShell>
    );
  }

  const headerRight = (
    <div className="flex space-x-2">
      <button
        onClick={() => fetchVPSData()}
        className="px-3 py-1.5 bg-white/80 backdrop-blur border border-white/40 text-slate-700 text-sm rounded-lg hover:border-secondary-500 transition"
      >
        <svg className="h-4 w-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Refresh
      </button>
      <button
        onClick={() => navigate('/vps#packages')}
        className="bg-secondary-500 text-primary-navy text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-secondary-600 transition"
      >
        <svg className="h-4 w-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Deploy New VPS
      </button>
    </div>
  );

  return (
    <DashShell
      title="VPS Management"
      subtitle="Deploy and manage your virtual private servers"
      right={headerRight}
    >
      <div>
        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-600 text-sm underline ml-4">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-green-600 text-sm underline ml-4">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-navy to-primary-800 rounded-lg flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total VPS</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {vpsInstances.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Running</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {vpsInstances.filter(vps => vps.status === 'running').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Monthly Cost</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ${vpsInstances.reduce((sum, vps) => sum + vps.monthly_price, 0).toFixed(2)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total CPU Cores</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {vpsInstances.reduce((sum, vps) => sum + vps.cpu_cores, 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* VPS Instances */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent"></div>
          
          <div className="relative z-10">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your VPS Instances</h2>
                  <p className="text-sm text-gray-600">Manage and monitor your virtual servers</p>
                </div>
                <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
                  <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-2"></span>
                  <span className="text-xs font-semibold text-gray-700">Live Status</span>
                </div>
              </div>
            </div>

            <div className="p-6">
              {vpsInstances.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No VPS Instances</h3>
                  <p className="mt-1 text-sm text-gray-500">Deploy your first VPS to get started.</p>
                  <button
                    onClick={() => navigate('/vps#packages')}
                    className="mt-6 inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg hover:from-orange-600 hover:to-yellow-600 transition-all duration-200"
                  >
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Deploy Your First VPS
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {vpsInstances.map((vps) => {
                    const metrics = vpsMetrics[vps.id];
                    return (
                      <div key={vps.id} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                        <div className="relative z-10 p-6 pointer-events-auto">
                          <div className="flex items-center justify-between mb-4">
                            <button 
                              onClick={() => navigate(`/customer/vps/${vps.id}`)}
                              className="text-lg font-bold text-primary-navy hover:text-orange-600 transition-colors relative z-20"
                            >
                              {vps.name}
                            </button>
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(vps.status)}`}>
                              {vps.status}
                            </span>
                          </div>

                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Plan:</span>
                              <span className="font-medium text-gray-900">{vps.plan_name}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Resources:</span>
                              <span className="font-medium text-gray-900">
                                {vps.cpu_cores} vCPU • {Math.round(vps.memory_mb / 1024)}GB RAM
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">IP Address:</span>
                              <span className="font-mono text-sm text-gray-900">
                                {vps.ipv4_address || 'Pending...'}
                              </span>
                            </div>
                            {metrics && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Uptime:</span>
                                <span className="font-medium text-gray-900">{formatUptime(metrics.uptime)}</span>
                              </div>
                            )}
                          </div>

                          {/* Resource Usage */}
                          {metrics && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-600">CPU</span>
                                    <span className="text-xs font-medium">{metrics.cpu_usage.toFixed(1)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-500"
                                      style={{ width: `${Math.min(metrics.cpu_usage, 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-600">Memory</span>
                                    <span className="text-xs font-medium">{metrics.memory_usage.toFixed(1)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                                      style={{ width: `${Math.min(metrics.memory_usage, 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Control Buttons */}
                        <div className="bg-white border-t border-gray-200 px-6 py-4 relative z-20 pointer-events-auto">
                          <div className="grid grid-cols-2 gap-2 relative z-30">
                            {vps.status === 'stopped' && (
                              <>
                                <button
                                  onClick={() => handleVPSControl(vps.id, 'start')}
                                  className="flex items-center justify-center px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Start
                                </button>
                                <button
                                  onClick={() => navigate(`/customer/vps/${vps.id}`)}
                                  className="flex items-center justify-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  Manage
                                </button>
                              </>
                            )}
                            
                            {vps.status === 'running' && (
                              <>
                                <button
                                  onClick={() => handleVPSControl(vps.id, 'restart')}
                                  className="flex items-center justify-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Restart
                                </button>
                                <button
                                  onClick={() => handleVPSControl(vps.id, 'stop')}
                                  className="flex items-center justify-center px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                                  </svg>
                                  Stop
                                </button>
                              </>
                            )}
                            
                            <button
                              onClick={() => navigate(`/customer/vps/${vps.id}?tab=console`)}
                              className="flex items-center justify-center px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors col-span-2"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Console Access
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <button 
            className="flex items-center p-4 bg-white hover:bg-orange-50 rounded-xl border border-gray-200 transition-colors duration-200 group shadow"
            onClick={() => navigate('/vps#packages')}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-primary-navy to-primary-800 group-hover:from-orange-500 group-hover:to-yellow-500 rounded-lg flex items-center justify-center mr-4 transition-all duration-200">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Deploy New VPS</p>
              <p className="text-sm text-gray-500">Create a new server instance</p>
            </div>
          </button>
          
          <button 
            className="flex items-center p-4 bg-white hover:bg-orange-50 rounded-xl border border-gray-200 transition-colors duration-200 group shadow"
            onClick={() => setActiveView('backups')}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-primary-navy to-primary-800 group-hover:from-orange-500 group-hover:to-yellow-500 rounded-lg flex items-center justify-center mr-4 transition-all duration-200">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Backups</p>
              <p className="text-sm text-gray-500">Manage snapshots & backups</p>
            </div>
          </button>
          
          <button 
            className="flex items-center p-4 bg-white hover:bg-orange-50 rounded-xl border border-gray-200 transition-colors duration-200 group shadow"
            onClick={() => navigate('/dashboard/support')}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-primary-navy to-primary-800 group-hover:from-orange-500 group-hover:to-yellow-500 rounded-lg flex items-center justify-center mr-4 transition-all duration-200">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">Support</p>
              <p className="text-sm text-gray-500">Get help with your VPS</p>
            </div>
          </button>
        </div>
      </div>
    </DashShell>
  );
};

export default AdvancedVPSDashboard;